import { Empty } from 'antd'
import type { UsageCategory } from './teamTypes'

/**
 * VistoeraTASK-00529 - tiny dependency-free SVG charts shared by 积分管理 and
 * 使用统计 (the project ships no chart library). Pure presentational: callers
 * pass real numbers; an all-zero / empty input renders an Empty state, never a
 * flat fake line, and nothing here can produce NaN / Infinity coordinates.
 */

export const CATEGORY_COLORS: Record<UsageCategory, string> = {
  image: 'var(--tm-accent)',
  video: '#8b5cf6',
  text: '#22c55e',
  audio: '#f59e0b',
  other: '#94a3b8',
}

export interface ChartSeries {
  key: string
  name: string
  values: number[]
  kind: 'line' | 'bar'
  color: string
  dashed?: boolean
}

const safe = (n: number): number => (Number.isFinite(n) && n > 0 ? n : 0)

export function TrendChart({
  labels,
  series,
  sharedScale = false,
  format,
  ariaLabel,
  emptyText,
}: {
  labels: string[]
  series: ChartSeries[]
  sharedScale?: boolean
  format: (seriesKey: string, v: number) => string
  ariaLabel: string
  emptyText: string
}) {
  const n = labels.length
  const maxAll = Math.max(0, ...series.flatMap((s) => s.values.map(safe)))
  if (!n || maxAll <= 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />

  const W = 640
  const H = 210
  const padL = 10
  const padR = 10
  const padT = 12
  const padB = 24
  const innerW = W - padL - padR
  const innerH = H - padT - padB
  const slot = innerW / n
  const xAt = (i: number) => padL + slot * i + slot / 2
  const labelEvery = Math.max(1, Math.ceil(n / 8))

  const scaleOf = (s: ChartSeries) => (sharedScale ? maxAll : Math.max(...s.values.map(safe), 0))
  const yAt = (v: number, max: number) => padT + innerH * (1 - (max > 0 ? safe(v) / max : 0))
  const bars = series.filter((s) => s.kind === 'bar')
  const lines = series.filter((s) => s.kind === 'line')

  return (
    <svg className="tm-trend-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} className="tm-chart-axis" />
      {bars.map((s) => {
        const max = scaleOf(s)
        const bw = Math.max(2, Math.min(22, slot * 0.55))
        return (
          <g key={s.key}>
            {s.values.map((v, i) =>
              safe(v) > 0 ? (
                <rect
                  key={labels[i]}
                  x={xAt(i) - bw / 2}
                  y={yAt(v, max)}
                  width={bw}
                  height={Math.max(1, H - padB - yAt(v, max))}
                  rx={2}
                  fill={s.color}
                  opacity={0.35}
                />
              ) : null,
            )}
          </g>
        )
      })}
      {lines.map((s) => {
        const max = scaleOf(s)
        const pts = s.values.map((v, i) => [xAt(i), yAt(v, max)] as const)
        const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
        return (
          <g key={s.key}>
            <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeDasharray={s.dashed ? '5 4' : undefined} />
            {n <= 31 &&
              pts.map(([x, y], i) => (
                <circle key={labels[i]} cx={x} cy={y} r={2.5} fill={s.color} />
              ))}
          </g>
        )
      })}
      {labels.map((label, i) => (
        <g key={label}>
          <rect x={padL + slot * i} y={padT} width={slot} height={innerH} fill="transparent">
            <title>{`${label}\n${series.map((s) => `${s.name}: ${format(s.key, safe(s.values[i] ?? 0))}`).join('\n')}`}</title>
          </rect>
          {i % labelEvery === 0 && (
            <text x={xAt(i)} y={H - 7} textAnchor="middle" className="tm-trend-label">
              {label.slice(5)}
            </text>
          )}
        </g>
      ))}
    </svg>
  )
}

export function DonutChart({ items, ariaLabel }: { items: Array<{ key: string; value: number; color: string }>; ariaLabel: string }) {
  const R = 52
  const C = 2 * Math.PI * R
  const total = items.reduce((s, i) => s + safe(i.value), 0)
  let offset = 0
  return (
    <svg className="tm-donut-svg" viewBox="0 0 140 140" role="img" aria-label={ariaLabel}>
      <circle cx="70" cy="70" r={R} fill="none" stroke="var(--tm-border)" strokeWidth="16" />
      {total > 0 &&
        items.map((it) => {
          const len = (safe(it.value) / total) * C
          const el = (
            <circle
              key={it.key}
              cx="70"
              cy="70"
              r={R}
              fill="none"
              stroke={it.color}
              strokeWidth="16"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 70 70)"
            />
          )
          offset += len
          return el
        })}
    </svg>
  )
}

export function HBars({
  rows,
  color = 'var(--tm-accent)',
}: {
  rows: Array<{ key: string; label: string; value: number; right: string }>
  color?: string
}) {
  const max = Math.max(0, ...rows.map((r) => safe(r.value)))
  return (
    <ul className="tm-hbars">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="tm-hbar-top">
            <span className="tm-hbar-label">{r.label}</span>
            <strong>{r.right}</strong>
          </div>
          <div className="tm-hbar-track">
            <div className="tm-hbar-fill" style={{ width: `${max > 0 ? Math.round((safe(r.value) / max) * 100) : 0}%`, background: color }} />
          </div>
        </li>
      ))}
    </ul>
  )
}
